/**
 * Musicseerr API Client
 * Interfaces with a Musicseerr instance to search and request albums
 */

export interface AlbumSearchResult {
  id: string;
  title: string;
  artist: string;
  year?: number;
  coverArt?: string;
  mbid?: string;
}

export class MusicseerrClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    // Remove trailing slash
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  /**
   * Test connection to Musicseerr instance
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
      }

      return true;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to connect to Musicseerr: ${error.message}`);
      }
      throw new Error('Failed to connect to Musicseerr');
    }
  }

  /**
   * Search for an album by artist and title
   */
  async searchAlbum(artist: string, album: string): Promise<AlbumSearchResult[]> {
    try {
      // Try searching with both artist and album
      const query = `${artist} ${album}`.trim();
      
      const response = await fetch(`${this.baseUrl}/api/v1/search?q=${encodeURIComponent(query)}&type=album`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Map the response to our interface
      // Note: Actual API response format may vary - adjust as needed
      return this.mapSearchResults(data, artist, album);
    } catch (error) {
      console.error('Search error:', error);
      
      // If the combined search fails, try MusicBrainz directly
      return this.searchMusicBrainz(artist, album);
    }
  }

  /**
   * Fallback: Search MusicBrainz directly
   */
  private async searchMusicBrainz(artist: string, album: string): Promise<AlbumSearchResult[]> {
    try {
      const query = `artist:"${artist}" AND release:"${album}"`;
      const url = `https://musicbrainz.org/ws/2/release/?query=${encodeURIComponent(query)}&fmt=json&limit=5`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'MusicseerrSync/1.0 (https://github.com/yourusername/musicseerr-sync)',
        },
      });

      if (!response.ok) {
        throw new Error(`MusicBrainz search failed: ${response.status}`);
      }

      const data = await response.json();
      
      return (data.releases || []).map((release: any) => ({
        id: release.id,
        title: release.title,
        artist: release['artist-credit']?.[0]?.name || artist,
        year: release.date ? parseInt(release.date.split('-')[0], 10) : undefined,
        mbid: release.id,
      }));
    } catch (error) {
      console.error('MusicBrainz search error:', error);
      return [];
    }
  }

  /**
   * Map Musicseerr API response to our interface
   */
  private mapSearchResults(data: any, searchArtist: string, searchAlbum: string): AlbumSearchResult[] {
    // Handle different possible response formats
    let results: any[] = [];
    
    if (Array.isArray(data)) {
      results = data;
    } else if (data.results && Array.isArray(data.results)) {
      results = data.results;
    } else if (data.albums && Array.isArray(data.albums)) {
      results = data.albums;
    }

    return results.map(item => ({
      id: item.id || item.mbid || item.musicbrainz_id || '',
      title: item.title || item.name || item.album || searchAlbum,
      artist: item.artist || item.artist_name || searchArtist,
      year: item.year || item.release_year,
      coverArt: item.cover_art || item.artwork || item.image,
      mbid: item.mbid || item.musicbrainz_id || item.id,
    }));
  }

  /**
   * Request an album to be downloaded via Lidarr
   */
  async requestAlbum(albumId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          albumId,
          // Add any additional parameters required by the API
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          errorData.error || 
          `Request failed: ${response.status} ${response.statusText}`
        );
      }

      return true;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to request album');
    }
  }

  /**
   * Get request status for an album
   */
  async getRequestStatus(albumId: string): Promise<string> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/request/${albumId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        return 'unknown';
      }

      const data = await response.json();
      return data.status || 'unknown';
    } catch (error) {
      console.error('Failed to get request status:', error);
      return 'unknown';
    }
  }

  /**
   * Check if an album is already in the library
   */
  async isAlbumInLibrary(albumId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/library/album/${albumId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Failed to check library status:', error);
      return false;
    }
  }
}
